import { useMutation } from '@apollo/client/react';
import { PipelineStatusBadge } from '@optools/components/pipelines/pipeline-status-badge';
import { Badge } from '@optools/components/ui/badge';
import { Card } from '@optools/components/ui/card';
import { cn } from '@optools/components/ui/utils';
import { ASSIGN_DEV_PIPELINE_CONFIG } from '@optools/services/operator-gateway/qa-tools/mutations';
import { PIPELINE_MONITORS } from '@optools/services/operator-gateway/qa-tools/queries';
import { PipelineMonitorsQuery } from '@optools/services/operator-gateway/types/generated/graphql';
import { toast } from 'sonner';

import { getConfigNumberFromLabel } from '../utils/pipeline-monitors';
import { TeamPicker } from './team-picker';

export interface DevPipelineMonitorCardProps {
  monitor: PipelineMonitorsQuery['pipelineMonitors'][number];
}

export function DevPipelineMonitorCard({ monitor }: DevPipelineMonitorCardProps) {
  const { eventPipeline, pipelineConfig } = monitor;
  const [assignDevPipelineConfig, { loading }] = useMutation(ASSIGN_DEV_PIPELINE_CONFIG, {
    awaitRefetchQueries: true,
    onCompleted: ({ assignDevPipelineConfig }) => {
      if (!assignDevPipelineConfig) {
        toast.error('Team Assignment Error', {
          description: 'Failed to assign team to pipeline config',
          duration: Infinity,
        });
      }
    },
    refetchQueries: [{ query: PIPELINE_MONITORS }],
  });

  if (!pipelineConfig) return null;

  const { fronthaulDecoderServiceLabel, id, teamId } = pipelineConfig;

  const handleSelectTeam = (newTeamId: number) => {
    assignDevPipelineConfig({ variables: { pipelineId: id, teamId: newTeamId } });
  };

  const isAvailable = !eventPipeline;

  return (
    <Card className="h-full p-4">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <span className="mb-1 flex gap-2">
            <h2 className="font-mono text-lg font-medium">
              {getConfigNumberFromLabel(fronthaulDecoderServiceLabel ?? '')}
            </h2>
            <Badge className="self-center" variant="outline">
              {id}
            </Badge>
          </span>
          <p className="text-xs text-primary/80">{fronthaulDecoderServiceLabel}</p>
        </div>

        <Badge
          className={cn(
            isAvailable ? 'border-green-500 text-green-500' : 'border-yellow-500 text-yellow-500',
          )}
          variant="outline">
          <span className="mr-0.5 size-2 rounded-full bg-current" />
          {isAvailable ? 'Available' : 'In Use'}
        </Badge>
      </div>

      <TeamPicker
        disabled={!isAvailable}
        loading={loading}
        message="Team assignment is disabled when there is an active pipeline"
        selectedTeamId={teamId}
        title="Assigned Team"
        onSelectTeam={handleSelectTeam}
      />

      {!isAvailable && (
        <Card className="mt-2 flex items-center justify-between rounded-md p-2">
          <h3 className="text-sm">Pipeline Status</h3>
          <PipelineStatusBadge status={eventPipeline?.status} />
        </Card>
      )}
    </Card>
  );
}
